import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  createPermission,
  createRole,
  deletePermission,
  getAllPermissions,
  getAllRoles,
  updatePermission,
  updateRolePermissions,
} from '../api/accessControlApi';
import type { Permission, PermissionInput, Role, RoleInput } from '../types';

type RolePermissionDrafts = Record<number, number[]>;

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Đã xảy ra lỗi không xác định.';

const buildRolePermissionDrafts = (roles: Role[]): RolePermissionDrafts =>
  Object.fromEntries(
    roles.map((role) => [role.id, role.permissions.map((permission) => permission.id)]),
  );

const haveSamePermissionIds = (firstIds: number[], secondIds: number[]): boolean => {
  if (firstIds.length !== secondIds.length) return false;
  const secondIdSet = new Set(secondIds);
  return firstIds.every((permissionId) => secondIdSet.has(permissionId));
};

export const useAccessControl = () => {
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolePermissionDrafts, setRolePermissionDrafts] = useState<RolePermissionDrafts>({});
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadAccessControl = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setLoadError('');

    try {
      const [permissionList, roleList] = await Promise.all([
        getAllPermissions(),
        getAllRoles(),
      ]);
      if (requestId !== requestIdRef.current) return;

      setPermissions(permissionList);
      setRoles(roleList);
      setRolePermissionDrafts(buildRolePermissionDrafts(roleList));
      setHasLoaded(true);
    } catch (error: unknown) {
      if (requestId === requestIdRef.current) {
        setLoadError(getErrorMessage(error));
        setHasLoaded(true);
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAccessControl();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadAccessControl]);

  const dirtyRoleIds = useMemo(() => roles
    .filter((role) => !haveSamePermissionIds(
      rolePermissionDrafts[role.id] ?? [],
      role.permissions.map((permission) => permission.id),
    ))
    .map((role) => role.id), [rolePermissionDrafts, roles]);

  const toggleRolePermission = (roleId: number, permissionId: number, checked: boolean) => {
    setRolePermissionDrafts((currentDrafts) => {
      const currentPermissionIds = currentDrafts[roleId] ?? [];
      const nextPermissionIds = checked
        ? Array.from(new Set([...currentPermissionIds, permissionId]))
        : currentPermissionIds.filter((currentId) => currentId !== permissionId);
      return { ...currentDrafts, [roleId]: nextPermissionIds };
    });
  };

  const saveRolePermissions = async (role: Role) => {
    const actionKey = `role-${role.id}`;
    setPendingAction(actionKey);

    try {
      const permissionIds = rolePermissionDrafts[role.id] ?? [];
      await updateRolePermissions(role.id, permissionIds);
      setRoles((currentRoles) => currentRoles.map((currentRole) =>
        currentRole.id === role.id
          ? {
            ...currentRole,
            permissions: permissions.filter((permission) => permissionIds.includes(permission.id)),
          }
          : currentRole,
      ));
      message.success(t('admin.settings.notices.role_updated', { role: role.name }));
    } catch (error: unknown) {
      message.error(getErrorMessage(error));
    } finally {
      setPendingAction(null);
    }
  };

  const savePermission = async (
    input: PermissionInput,
    editingPermission: Permission | null,
  ): Promise<boolean> => {
    setPendingAction('permission');

    try {
      if (editingPermission) {
        await updatePermission(editingPermission.id, input);
      } else {
        await createPermission(input);
      }
      message.success(
        editingPermission
          ? t('admin.settings.notices.permission_updated')
          : t('admin.settings.notices.permission_created'),
      );
      await loadAccessControl();
      return true;
    } catch (error: unknown) {
      message.error(getErrorMessage(error));
      return false;
    } finally {
      setPendingAction(null);
    }
  };

  const removePermission = async (permission: Permission): Promise<boolean> => {
    setPendingAction(`permission-delete-${permission.id}`);

    try {
      await deletePermission(permission.id);
      message.success(
        t('admin.settings.notices.permission_deleted', { permission: permission.name }),
      );
      await loadAccessControl();
      return true;
    } catch (error: unknown) {
      message.error(getErrorMessage(error));
      return false;
    } finally {
      setPendingAction(null);
    }
  };

  const addRole = async (input: RoleInput): Promise<boolean> => {
    setPendingAction('role');

    try {
      await createRole(input);
      message.success(t('admin.settings.notices.role_created'));
      await loadAccessControl();
      return true;
    } catch (error: unknown) {
      message.error(getErrorMessage(error));
      return false;
    } finally {
      setPendingAction(null);
    }
  };

  return {
    permissions,
    roles,
    rolePermissionDrafts,
    dirtyRoleIds,
    isLoading,
    hasLoaded,
    loadError,
    pendingAction,
    loadAccessControl,
    toggleRolePermission,
    saveRolePermissions,
    savePermission,
    removePermission,
    addRole,
  };
};
